import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 500);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToNext = () => {
    const nextSection = document.getElementById('stats');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section ref={heroRef} className="relative h-screen w-full overflow-hidden bg-black">
      {/* Animated 3D Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated Grid */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 50, 37, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 50, 37, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '100px 100px',
            transform: `
              perspective(500px) 
              rotateX(60deg) 
              translateZ(-100px)
              translateX(${mousePosition.x * 50}px)
              translateY(${mousePosition.y * 50}px)
            `,
            transition: 'transform 0.3s ease-out'
          }}
        />

        {/* Floating Particles */}
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-red-600 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
              opacity: Math.random() * 0.5 + 0.3,
              transform: `translateZ(${Math.random() * 100}px)`
            }}
          />
        ))}

        {/* 3D Film Reels */}
        <div 
          className="absolute top-1/4 left-1/4 w-32 h-32 opacity-30"
          style={{
            transform: `
              perspective(1000px)
              rotateY(${mousePosition.x * 30}deg)
              rotateX(${mousePosition.y * 30}deg)
              translateZ(50px)
            `,
            transition: 'transform 0.3s ease-out'
          }}
        >
          <div className="w-full h-full border-8 border-red-600 rounded-full animate-spin-slow" />
          <div className="absolute inset-0 border-8 border-white rounded-full animate-spin-reverse" />
        </div>

        <div 
          className="absolute bottom-1/4 right-1/4 w-24 h-24 opacity-30"
          style={{
            transform: `
              perspective(1000px)
              rotateY(${-mousePosition.x * 30}deg)
              rotateX(${-mousePosition.y * 30}deg)
              translateZ(30px)
            `,
            transition: 'transform 0.3s ease-out'
          }}
        >
          <div className="w-full h-full border-8 border-red-600 rounded-full animate-spin-reverse" />
        </div>

        {/* 3D Camera Shape */}
        <div 
          className="absolute top-1/2 right-1/3 w-40 h-28 opacity-20"
          style={{
            transform: `
              perspective(1000px)
              rotateY(${mousePosition.x * 20}deg)
              rotateX(${mousePosition.y * 20}deg)
              translateZ(80px)
            `,
            transition: 'transform 0.3s ease-out'
          }}
        >
          <div className="w-full h-full bg-gradient-to-br from-red-600 to-transparent border-2 border-red-600 rounded-lg relative">
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-16 h-16 bg-red-600 rounded-full border-4 border-white" />
          </div>
        </div>

        {/* Animated Light Rays */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-red-600 to-transparent animate-pulse" />
          <div className="absolute top-0 left-1/2 w-1 h-full bg-gradient-to-b from-white to-transparent animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-red-600 to-transparent animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/50 to-black" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black" />
      </div>

      {/* Content Layer */}
      <div className="relative h-full flex items-center justify-center z-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div 
            className={`text-center max-w-5xl mx-auto transform transition-all duration-1000 ${
              loaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
            style={{
              transform: `
                perspective(1000px)
                translateY(${loaded ? mousePosition.y * 10 : 40}px)
                translateX(${mousePosition.x * 10}px)
                rotateX(${mousePosition.y * 2}deg)
                rotateY(${mousePosition.x * 2}deg)
              `,
              transition: 'transform 0.3s ease-out, opacity 1s ease-out'
            }}
          >
            {/* Main Headline */}
            <h1 className="text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold mb-6 leading-tight tracking-tight">
              <span 
                className="inline-block animate-gradient"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #FF3225 50%, #ffffff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  backgroundSize: '200% auto',
                  textShadow: '0 0 40px rgba(255, 50, 37, 0.5)'
                }}
              >
                RECfolk
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-2xl md:text-3xl lg:text-4xl text-white/90 mb-4 font-light">
              Crafting Visual Stories That{' '}
              <span className="text-red-600 font-semibold animate-pulse-subtle">Captivate</span>
            </p>

            <p className="text-lg md:text-xl text-white/70 mb-12 max-w-3xl mx-auto">
              Award-winning commercials and documentaries for brands and agencies worldwide
            </p>

            {/* CTA Buttons */}
            <div 
              className={`flex flex-wrap justify-center gap-6 mb-16 transform transition-all duration-1000 delay-300 ${
                loaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              <button 
                onClick={() => document.getElementById('showreel').scrollIntoView({ behavior: 'smooth' })}
                className="group relative bg-red-600 text-white px-10 py-5 rounded-full transition-all duration-300 text-base font-semibold tracking-wide uppercase shadow-lg shadow-red-600/50 hover:shadow-2xl hover:shadow-red-600/60 transform hover:scale-110 hover:-translate-y-1 overflow-hidden"
              >
                <span className="relative z-10">Watch Showreel</span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
              
              <button 
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="group relative bg-white/10 backdrop-blur-md text-white px-10 py-5 rounded-full hover:bg-white/20 transition-all duration-300 text-base font-semibold tracking-wide uppercase border-2 border-white/30 hover:border-red-600 transform hover:scale-110 hover:-translate-y-1"
              >
                <span className="relative z-10">Start a Project</span>
              </button>
            </div>

            {/* Stats Preview */}
            <div 
              className={`flex flex-wrap justify-center gap-8 md:gap-16 text-white/80 transform transition-all duration-1000 delay-500 ${
                loaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
            >
              <div className="text-center group">
                <div className="text-3xl md:text-4xl font-bold text-red-600 mb-1 group-hover:scale-110 transition-transform duration-300">150+</div>
                <div className="text-sm uppercase tracking-wider">Projects</div>
              </div>
              <div className="text-center group">
                <div className="text-3xl md:text-4xl font-bold text-red-600 mb-1 group-hover:scale-110 transition-transform duration-300">50+</div>
                <div className="text-sm uppercase tracking-wider">Brands</div>
              </div>
              <div className="text-center group">
                <div className="text-3xl md:text-4xl font-bold text-red-600 mb-1 group-hover:scale-110 transition-transform duration-300">15+</div>
                <div className="text-sm uppercase tracking-wider">Awards</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToNext}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-white/60 hover:text-red-600 transition-colors duration-300 animate-bounce-slow"
        aria-label="Scroll down"
      >
        <ChevronDown size={40} />
      </button>
    </section>
  );
};

export default HeroSection;
